import {
  TableHTMLAttributes,
  ThHTMLAttributes,
  TdHTMLAttributes,
  HTMLAttributes,
  forwardRef,
  ReactNode,
} from 'react';
import './Table.css';

export type TableSize = 'sm' | 'md' | 'lg';
export type SortDirection = 'asc' | 'desc' | null;

export interface TableProps extends TableHTMLAttributes<HTMLTableElement> {
  /** Size variant */
  size?: TableSize;
  /** Enable striped rows */
  striped?: boolean;
  /** Enable hover effect on rows */
  hoverable?: boolean;
  /** Fixed layout for consistent column widths */
  fixedLayout?: boolean;
}

export interface TableHeaderProps extends HTMLAttributes<HTMLTableSectionElement> {}

export interface TableBodyProps extends HTMLAttributes<HTMLTableSectionElement> {}

export interface TableFooterProps extends HTMLAttributes<HTMLTableSectionElement> {}

export interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  /** Highlight the row as selected */
  selected?: boolean;
}

export interface TableHeadCellProps extends ThHTMLAttributes<HTMLTableCellElement> {
  /** Enable sorting for this column */
  sortable?: boolean;
  /** Current sort direction */
  sortDirection?: SortDirection;
  /** Callback when sort is triggered */
  onSort?: () => void;
}

export interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  /** Truncate text with ellipsis */
  truncate?: boolean;
}

/**
 * Table component for displaying tabular data.
 *
 * @example
 * ```tsx
 * <Table>
 *   <Table.Header>
 *     <Table.Row>
 *       <Table.HeadCell sortable sortDirection="asc">Name</Table.HeadCell>
 *       <Table.HeadCell>Status</Table.HeadCell>
 *     </Table.Row>
 *   </Table.Header>
 *   <Table.Body>
 *     <Table.Row>
 *       <Table.Cell>Item 1</Table.Cell>
 *       <Table.Cell>Active</Table.Cell>
 *     </Table.Row>
 *   </Table.Body>
 * </Table>
 * ```
 */
export const Table = forwardRef<HTMLTableElement, TableProps>(
  (
    {
      size = 'md',
      striped = false,
      hoverable = false,
      fixedLayout = false,
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    const classNames = [
      'ui-table',
      `ui-table--${size}`,
      striped && 'ui-table--striped',
      hoverable && 'ui-table--hoverable',
      fixedLayout && 'ui-table--fixed',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <table ref={ref} className={classNames} {...props}>
        {children}
      </table>
    );
  }
) as TableComponent;

Table.displayName = 'Table';

const TableHeader = forwardRef<HTMLTableSectionElement, TableHeaderProps>(
  ({ className = '', children, ...props }, ref) => {
    const classNames = ['ui-table__header', className]
      .filter(Boolean)
      .join(' ');

    return (
      <thead ref={ref} className={classNames} {...props}>
        {children}
      </thead>
    );
  }
);

TableHeader.displayName = 'Table.Header';

const TableBody = forwardRef<HTMLTableSectionElement, TableBodyProps>(
  ({ className = '', children, ...props }, ref) => {
    const classNames = ['ui-table__body', className].filter(Boolean).join(' ');

    return (
      <tbody ref={ref} className={classNames} {...props}>
        {children}
      </tbody>
    );
  }
);

TableBody.displayName = 'Table.Body';

const TableFooter = forwardRef<HTMLTableSectionElement, TableFooterProps>(
  ({ className = '', children, ...props }, ref) => {
    const classNames = ['ui-table__footer', className]
      .filter(Boolean)
      .join(' ');

    return (
      <tfoot ref={ref} className={classNames} {...props}>
        {children}
      </tfoot>
    );
  }
);

TableFooter.displayName = 'Table.Footer';

const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ selected = false, className = '', children, ...props }, ref) => {
    const classNames = [
      'ui-table__row',
      selected && 'ui-table__row--selected',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <tr ref={ref} className={classNames} {...props}>
        {children}
      </tr>
    );
  }
);

TableRow.displayName = 'Table.Row';

const SortIcon = ({ direction }: { direction: SortDirection }) => (
  <span className="ui-table__sort-icon" aria-hidden="true">
    {direction === 'asc' && '↑'}
    {direction === 'desc' && '↓'}
    {direction === null && '↕'}
  </span>
);

const TableHeadCell = forwardRef<HTMLTableCellElement, TableHeadCellProps>(
  (
    {
      sortable = false,
      sortDirection,
      onSort,
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    const classNames = [
      'ui-table__head-cell',
      sortable && 'ui-table__head-cell--sortable',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const handleClick = () => {
      if (sortable && onSort) {
        onSort();
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (sortable && onSort && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        onSort();
      }
    };

    return (
      <th
        ref={ref}
        className={classNames}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={sortable ? 0 : undefined}
        role={sortable ? 'columnheader button' : 'columnheader'}
        aria-sort={
          sortDirection === 'asc'
            ? 'ascending'
            : sortDirection === 'desc'
              ? 'descending'
              : undefined
        }
        {...props}
      >
        <span className="ui-table__head-cell-content">
          {children}
          {sortable && <SortIcon direction={sortDirection ?? null} />}
        </span>
      </th>
    );
  }
);

TableHeadCell.displayName = 'Table.HeadCell';

const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ truncate = false, className = '', children, ...props }, ref) => {
    const classNames = [
      'ui-table__cell',
      truncate && 'ui-table__cell--truncate',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <td ref={ref} className={classNames} {...props}>
        {children}
      </td>
    );
  }
);

TableCell.displayName = 'Table.Cell';

// Compound component type
interface TableComponent extends React.ForwardRefExoticComponent<
  TableProps & React.RefAttributes<HTMLTableElement>
> {
  Header: typeof TableHeader;
  Body: typeof TableBody;
  Footer: typeof TableFooter;
  Row: typeof TableRow;
  HeadCell: typeof TableHeadCell;
  Cell: typeof TableCell;
}

// Attach sub-components
Table.Header = TableHeader;
Table.Body = TableBody;
Table.Footer = TableFooter;
Table.Row = TableRow;
Table.HeadCell = TableHeadCell;
Table.Cell = TableCell;

// Table container for scroll support
export interface TableContainerProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export const TableContainer = forwardRef<HTMLDivElement, TableContainerProps>(
  ({ className = '', children, ...props }, ref) => {
    const classNames = ['ui-table-container', className]
      .filter(Boolean)
      .join(' ');

    return (
      <div ref={ref} className={classNames} {...props}>
        {children}
      </div>
    );
  }
);

TableContainer.displayName = 'TableContainer';
